import { useState } from "react";

import {
  Button,
  CopyField,
  ErrorState,
  Field,
  Input,
  Page,
  Panel,
  Skeleton,
  Stack,
  StatusBadge,
  Toolbar,
} from "@/components/ui";

import { PagedTable } from "../components/PagedTable";
import { useMintToken, usePublishTokens, useRevokeToken, type PublishToken } from "../lib/queries";

/** Minted here, shown once, revoked by the id the list handed out. */
export function Tokens() {
  const [name, setName] = useState("");
  const tokens = usePublishTokens();
  const mint = useMintToken();
  const revoke = useRevokeToken();

  if (tokens.isPending) {
    return (
      <Page title="Publish tokens" eyebrow="Publisher">
        <Skeleton height={200} />
      </Page>
    );
  }

  if (tokens.isError) {
    return (
      <Page title="Publish tokens" eyebrow="Publisher">
        <ErrorState
          headline="Tokens unavailable"
          onRetry={() => void tokens.refetch()}
          retryLabel="Retry"
        />
      </Page>
    );
  }

  return (
    <Page title="Publish tokens" eyebrow="Publisher">
      <Panel title="New token">
        <Stack gap={5}>
          <Toolbar fill>
            <Field label="Name">
              <Input
                value={name}
                onChange={(event) => {
                  setName(event.target.value);
                }}
              />
            </Field>
            <Button
              variant="primary"
              disabled={name.trim() === ""}
              loading={mint.isPending}
              onClick={() => {
                mint.mutate(name.trim());
                setName("");
              }}
            >
              Mint
            </Button>
          </Toolbar>
          {mint.isSuccess ? <CopyField value={mint.data.secret} label="Secret" group large /> : null}
          {mint.isError ? <ErrorState headline={mint.error.message} /> : null}
        </Stack>
      </Panel>

      <Panel title="Tokens">
        <Stack gap={5}>
          <PagedTable
            rows={tokens.data}
            caption="Publish tokens"
            density="comfortable"
            emptyHeadline="No tokens"
            placeholder="Find a token"
            match={(row, needle) => row.name.toLowerCase().includes(needle)}
            columns={[
              { key: "name", header: "Token", emphasis: true, mono: true },
              { key: "token_id", header: "Id", mono: true, muted: true },
              { key: "created_at", header: "Created", muted: true },
              {
                key: "last_used_at",
                header: "Last used",
                muted: true,
                render: (row: PublishToken) => row.last_used_at ?? "never",
              },
              {
                key: "state",
                header: "State",
                render: (row: PublishToken) =>
                  row.revoked_at == null ? (
                    <StatusBadge tone="success">live</StatusBadge>
                  ) : (
                    <StatusBadge tone="neutral">revoked</StatusBadge>
                  ),
              },
              {
                key: "revoke",
                header: "",
                align: "right",
                width: "110px",
                render: (row: PublishToken) => (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={row.revoked_at != null}
                    onClick={() => {
                      revoke.mutate(row.token_id);
                    }}
                  >
                    Revoke
                  </Button>
                ),
              },
            ]}
          />
          {revoke.isError ? <ErrorState headline={revoke.error.message} /> : null}
        </Stack>
      </Panel>
    </Page>
  );
}
